import React, { useState } from 'react';
import { Box, BoxProps } from '@mui/material';

interface SceneDetectionImageProps extends Omit<BoxProps, 'component'> {
  svgSrc: string;
  pngSrc: string;
  alt: string;
  fallbackText: string;
  rotation?: number;
}

const SceneDetectionImage: React.FC<SceneDetectionImageProps> = ({
  svgSrc,
  pngSrc,
  alt,
  fallbackText,
  rotation = 0,
  ...boxProps
}) => {
  const [imageError, setImageError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!imageError) {
      // Try PNG if SVG fails
      setImageError(true);
      const target = e.target as HTMLImageElement;
      target.src = pngSrc;
    } else {
      // If both fail, show fallback
      setShowFallback(true);
      const target = e.target as HTMLImageElement;
      target.style.display = 'none';
      
      // Create fallback content if it doesn't exist
      if (!target.parentElement?.querySelector('.fallback-content')) {
        const fallbackContainer = document.createElement('div');
        fallbackContainer.className = 'fallback-content';
        fallbackContainer.style.display = 'flex';
        fallbackContainer.style.alignItems = 'center';
        fallbackContainer.style.justifyContent = 'center';
        fallbackContainer.style.minHeight = '300px';
        fallbackContainer.style.backgroundColor = 'rgba(110, 58, 228, 0.1)';
        
        const fallbackTextElement = document.createElement('div');
        fallbackTextElement.textContent = fallbackText;
        fallbackTextElement.style.color = 'rgba(110, 58, 228, 0.7)';
        fallbackTextElement.style.fontSize = '1.5rem';
        fallbackTextElement.style.fontWeight = 'bold';
        
        fallbackContainer.appendChild(fallbackTextElement);
        target.parentElement!.appendChild(fallbackContainer);
      }
    }
  };

  if (showFallback) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '300px',
          borderRadius: 2,
          boxShadow: 3,
          bgcolor: 'rgba(110, 58, 228, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...boxProps.sx,
        }}
        {...boxProps}
      >
        <Box
          sx={{
            color: 'rgba(110, 58, 228, 0.7)',
            fontSize: '1.5rem',
            fontWeight: 'bold',
          }}
        >
          {fallbackText}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={imageError ? pngSrc : svgSrc}
      alt={alt}
      onError={handleError}
      sx={{
        width: '100%',
        height: 'auto',
        borderRadius: 2,
        boxShadow: 3,
        transform: `perspective(1000px) rotateY(${rotation}deg)`,
        transition: 'transform 0.3s ease-in-out',
        '&:hover': {
          transform: 'perspective(1000px) rotateY(0deg)',
        },
        ...boxProps.sx,
      }}
      {...boxProps}
    />
  );
};

export default SceneDetectionImage; 